import {inject} from '@angular/core';
import {CanActivateFn, Router, RouterStateSnapshot} from '@angular/router';
import {catchError, map} from 'rxjs/operators';
import {of} from 'rxjs';
import {AuthService} from '@app/services/auth.service';
import {UserService} from '@app/services/userService/user.service';
import {PermissionEnum} from '@app/enums/permissions/PermissionEnum';

export const DAILY_DASHBOARD_KEY = 'dailyDashboardShownOn';

/**
 * The "day" a visit belongs to, where a day starts at 04:00 local time. A
 * visit at 02:00 still counts as the previous day, so the next redirect is
 * due at 04:00 rather than at midnight.
 *
 * toDateString() (not toISOString()) — the key must be a *local* date, or a
 * late-evening visit in CEST would be stamped with the next UTC day.
 */
export function dashboardDayKey(now: Date = new Date()): string {
  const day = new Date(now);
  if (day.getHours() < 4) {
    day.setDate(day.getDate() - 1);
  }
  return day.toDateString();
}

/**
 * BRIEFD-7157: send dealers to the Dashboard once per day, on their first
 * /admin/* navigation after 04:00. Requires `runGuardsAndResolvers: 'always'`
 * on the route it is attached to — otherwise a tab left open overnight never
 * re-evaluates it.
 */
export const dailyDashboardGuard: CanActivateFn = (_route, state: RouterStateSnapshot) => {
  // Not logged in (or SSR, where there is no localStorage): AuthGuard on the
  // child routes owns that case. Bailing out also avoids firing a profile
  // request that would never resolve and hang the navigation.
  if (!inject(AuthService).isLoggedIn()) {
    return of(true);
  }

  const key = dashboardDayKey();
  if (localStorage.getItem(DAILY_DASHBOARD_KEY) === key) {
    return of(true);
  }

  const router = inject(Router);

  return inject(UserService).getUserProfile().pipe(
    map((profile) => {
      // No dashboard permission means the redirect would land on /403. Leave
      // the marker unset so the check runs again if permissions change.
      if (profile.permissions?.[PermissionEnum.DASHBOARD_DASHBOARD_VIEW] !== true) {
        return true;
      }

      localStorage.setItem(DAILY_DASHBOARD_KEY, key);

      return state.url.startsWith('/admin/dashboard')
        ? true
        : router.createUrlTree(['/admin/dashboard']);
    }),
    // Fail open. This guard sits on the /admin parent route, so letting a
    // failed profile request reject here would block navigation to every
    // admin page - a once-a-day convenience redirect must never be able to
    // take down the whole app.
    catchError(() => of(true))
  );
};
